/**
 * P0-PM-001: Background Installation Tracking
 *
 * Persists installation progress to localStorage and provides
 * real-time status across page navigation.
 */

import { useEffect, useState, useCallback } from 'react';

export interface InstallationProgress {
  instanceId: string;
  bobName: string;
  state: 'queued' | 'downloading' | 'downloaded' | 'extracting' | 'installing' | 'installed' | 'failed';
  progress: number;
  error?: string;
  startedAt: number; // timestamp
  updatedAt: number; // timestamp
}

const STORAGE_KEY = 'aiia-installation-progress';
const STALE_AFTER_MS = 1000 * 60 * 60; // 1 hour

/**
 * Hook for managing background installation progress
 *
 * Features:
 * - Persists to localStorage
 * - Syncs across tabs
 * - Auto-cleans stale installations
 * - Provides active installation count
 */
export function useInstallationProgress() {
  const [installations, setInstallations] = useState<Map<string, InstallationProgress>>(
    new Map()
  );

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as InstallationProgress[];
        const now = Date.now();

        // Filter out stale installations
        const active = parsed.filter((inst) => {
          const age = now - inst.updatedAt;
          const isComplete = inst.state === 'installed' || inst.state === 'failed';

          // Keep completed installations for 5 minutes
          if (isComplete) {
            return age < 1000 * 60 * 5;
          }

          // Keep active installations for 1 hour
          return age < STALE_AFTER_MS;
        });

        const map = new Map(active.map((inst) => [
          `${inst.instanceId}-${inst.bobName}`,
          inst
        ]));

        setInstallations(map);
      }
    } catch (error) {
      console.error('Failed to load installation progress:', error);
    }
  }, []);

  // Save to localStorage whenever installations change
  useEffect(() => {
    try {
      const array = Array.from(installations.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(array));
    } catch (error) {
      console.error('Failed to save installation progress:', error);
    }
  }, [installations]);

  // Sync across tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue) as InstallationProgress[];
          const map = new Map(parsed.map((inst) => [
            `${inst.instanceId}-${inst.bobName}`,
            inst
          ]));
          setInstallations(map);
        } catch (error) {
          console.error('Failed to sync installation progress:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Add or update installation
  const updateInstallation = useCallback((installation: InstallationProgress) => {
    setInstallations((prev) => {
      const next = new Map(prev);
      const key = `${installation.instanceId}-${installation.bobName}`;
      next.set(key, {
        ...installation,
        updatedAt: Date.now(),
      });
      return next;
    });
  }, []);

  // Remove installation (completed or cancelled)
  const removeInstallation = useCallback((instanceId: string, bobName: string) => {
    setInstallations((prev) => {
      const next = new Map(prev);
      next.delete(`${instanceId}-${bobName}`);
      return next;
    });
  }, []);

  // Clear all installations for an instance
  const clearInstance = useCallback((instanceId: string) => {
    setInstallations((prev) => {
      const next = new Map(prev);
      Array.from(next.keys()).forEach((key) => {
        if (key.startsWith(`${instanceId}-`)) {
          next.delete(key);
        }
      });
      return next;
    });
  }, []);

  // Get active installations (not completed/failed)
  const activeInstallations = Array.from(installations.values()).filter(
    (inst) => inst.state !== 'installed' && inst.state !== 'failed'
  );

  // Get completed installations
  const completedInstallations = Array.from(installations.values()).filter(
    (inst) => inst.state === 'installed'
  );

  // Get failed installations
  const failedInstallations = Array.from(installations.values()).filter(
    (inst) => inst.state === 'failed'
  );

  // Get installations for specific instance
  const getInstanceInstallations = useCallback((instanceId: string) => {
    return Array.from(installations.values()).filter(
      (inst) => inst.instanceId === instanceId
    );
  }, [installations]);

  // Check if any installations are active
  const hasActiveInstallations = activeInstallations.length > 0;

  return {
    installations: Array.from(installations.values()),
    activeInstallations,
    completedInstallations,
    failedInstallations,
    hasActiveInstallations,
    updateInstallation,
    removeInstallation,
    clearInstance,
    getInstanceInstallations,
  };
}
